import {observable, action, reaction} from 'mobx'
import {Api} from './api'
import decode from 'jwt-decode'
import {notification} from 'antd'
import Validator from 'validatorjs'
import {customNames, errorMessages, passResetRules} from '../signup/rules'

export class Auth {
  @observable email
  @observable password
  @observable password_repeat
  @observable isLoggedIn
  @observable refreshing
  @observable error
  @observable errors = {}
  @observable recaptcha_response
  @observable loading = false
  @observable validationErrors = {}

  constructor(rootStore) {
    this.root = rootStore
    this.api = new Api()
    this.isLoggedIn = localStorage.getItem('refreshToken') !== null
  }

  @action change(name, value) {
    this[name] = value
  }

  @action addValidationError(key, value) {
    this.validationErrors[key] = value
  }

  @action clearValidationErrors() {
    this.validationErrors = {}
  }

  @action async login(email = this.email, password = this.password) {
    this.error = null
    this.loading = true
    this.clearValidationErrors()
    try {
      const {access, refresh} = await this.api.obtainToken(email, password)
      localStorage.setItem('accessToken', access)
      localStorage.setItem('refreshToken', refresh)

      this.root.user.getUserData()
      this.isLoggedIn = true
    } catch (e) {
      if (Array.isArray(e.errors)) {
        this.error = e.errors[0].message
      } else if (e.field_errors) {
        for (let key in e.field_errors) {
          this.addValidationError(key, e.field_errors[key]?.[0]?.message)
        }
      }else {
        notification['error']({
          message: 'Failed to log in'
        })
      }
    } finally {
      this.loading = false
    }
  }

  @action async refreshToken() {
    if (this.refreshing) {
      return new Promise((resolve, reject) => {
        reaction(
          () => this.refreshing,
          refreshing => {
            if (!refreshing) {
              if (localStorage.getItem('refreshToken') !== null) {
                resolve()
              } else {
                reject()
              }
            }
          }
        )
      })
    }

    const refresh = localStorage.getItem('refreshToken')
    if (refresh === null) {
      this.logout()
      return Promise.reject()
    }

    const access = localStorage.getItem('accessToken')
    const accessLiveTime = access ? decode(access).exp - (new Date().getTime() / 1000 | 0) : 0
    if (accessLiveTime > 30) {
      return Promise.resolve()
    }

    this.refreshing = true
    try {
      const {access} = await this.api.refreshToken(refresh)
      localStorage.setItem('accessToken', access)
      this.refreshing = false
    } catch(e) { // eslint-disable-line
      this.refreshing = false
      this.logout()
    }
  }

  @action logout() {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    this.root.user.setDefaults()
    this.isLoggedIn = false
  }

  @action async resetPassword() {
    try {
      this.change('loading', true)
      const res = await this.api.resetPassword(this.email, this.recaptcha_response)
      if (res.code === 'ok') {
        this.change('passwordReset', true)
      }
    } catch (e) {
      notification['error']({
        message: 'Failed to reset your password',
        description: e.message || 'Unknown error'
      })
    }
    this.change('loading', false)
  }

  @action reset() {
    this.email = null
    this.password = null
    this.password_repeat = null
    this.errors = {}
    this.loading = false
  }

  @action async resetPasswordConfirm(recovery_key) {
    let credentials = {email: this.email, password: this.password, password_repeat: this.password_repeat}
    const validation = new Validator(credentials, passResetRules, errorMessages)
    if (validation.passes()) {
      this.loading = true
      try {
        await this.api.resetPasswordConfirm({recovery_key, ...credentials})
        this.complete = true

        const {email, password} = credentials
        this.root.auth.login(email, password)

        this.reset()
      } catch(e) {

        const errors = e.field_errors
        if (typeof errors === 'object' && errors !== null) {
          for (let name in errors) {
            if (errors[name]) {
              this.errors[name] = errors[name][0].message
            }
          }
        }

        notification['error']({
          message: 'Failed to change password',
          description: e.message || 'Unknown error'
        })
      } finally {
        this.loading = false
      }
    }
    if (validation.fails()) {
      const errors = validation.errors.all()
      for (let name in errors) {
        this.errors[name] = errors[name][0]
      }
    }
  }

  @action validateField(name) {
    let credentials = {email: this.email, password: this.password, password_repeat: this.password_repeat}
    const validation = new Validator(credentials, passResetRules, errorMessages)
    validation.setAttributeNames(customNames)
    if (validation.fails()) {
      let errors = {}
      errors[name] = validation.errors.first(name)
      this.errors = Object.assign(this.errors, errors)
    }
  }

  @action removeError(name) {
    if (this.errors[name] !== undefined) {
      const {...errors} = this.errors
      delete errors[name]
      this.errors = errors
    }
  }
}
